/**
 * University Edit Page
 * Admin page to edit university details
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ImageFieldWithPicker } from '@/components/admin/AssetPickerDialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Save,
  Loader2,
  Building2,
  Globe,
  MapPin,
  Star,
  Link as LinkIcon,
  Image
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const countries = [
  'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 
  'France', 'Netherlands', 'Ireland', 'New Zealand', 'Singapore',
  'United Arab Emirates', 'Switzerland', 'Sweden', 'Denmark', 'Japan',
  'South Korea', 'Italy', 'Spain', 'Austria', 'Belgium'
];

const UniversityEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, hasPermission, logout } = useCMSAuth();
  
  // Check if this is create mode (id is undefined or 'new')
  const isNewMode = !id || id === 'new';
  const [loading, setLoading] = useState(!isNewMode);
  const [saving, setSaving] = useState(false);
  const [university, setUniversity] = useState({
    name: '',
    short_name: '',
    country: '',
    city: '',
    state_province: '',
    website: '',
    logo_url: '',
    description: '',
    qs_rank: '',
    acceptance_rate: '',
    is_featured: false,
    is_partner: false,
    tuition_min: '',
    tuition_max: '',
    living_cost_min: '',
    living_cost_max: ''
  });

  const canWrite = hasPermission('universities', 'write');

  const handleUnauthorized = (res) => {
    if (res.status === 401) {
      toast.error('Session expired. Please log in again.');
      logout();
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!isNewMode) {
      fetchUniversity();
    }
  }, [id]);

  const fetchUniversity = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/cms/universities/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (handleUnauthorized(res)) return;
      
      if (res.ok) {
        const data = await res.json();
        setUniversity({
          name: data.name || '',
          short_name: data.short_name || '',
          country: data.country || '',
          city: data.city || '',
          state_province: data.state_province || '',
          website: data.website || '',
          logo_url: data.logo_url || '',
          description: data.description || '',
          qs_rank: data.qs_rank || '',
          acceptance_rate: data.acceptance_rate || '',
          is_featured: data.is_featured || false,
          is_partner: data.is_partner || false,
          tuition_min: data.tuition_min || '',
          tuition_max: data.tuition_max || '',
          living_cost_min: data.living_cost_min || '',
          living_cost_max: data.living_cost_max || ''
        });
      } else {
        toast.error('University not found');
        navigate('/admin/universities');
      }
    } catch (error) {
      console.error('Error fetching university:', error);
      toast.error('Failed to load university');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!university.name || !university.country) {
      toast.error('Name and country are required');
      return;
    }

    setSaving(true);
    try {
      const url = isNewMode 
        ? `${API_URL}/api/cms/universities` 
        : `${API_URL}/api/cms/universities/${id}`;
      const method = isNewMode ? 'POST' : 'PUT';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...university,
          qs_rank: university.qs_rank ? parseInt(university.qs_rank) : null,
          acceptance_rate: university.acceptance_rate ? parseFloat(university.acceptance_rate) : null,
          tuition_min: university.tuition_min ? parseInt(university.tuition_min) : null,
          tuition_max: university.tuition_max ? parseInt(university.tuition_max) : null,
          living_cost_min: university.living_cost_min ? parseInt(university.living_cost_min) : null,
          living_cost_max: university.living_cost_max ? parseInt(university.living_cost_max) : null
        })
      });

      if (res.ok) {
        toast.success(isNewMode ? 'University created successfully' : 'University updated successfully');
        navigate('/admin/universities');
      } else {
        const error = await res.json();
        toast.error(error.detail || `Failed to ${isNewMode ? 'create' : 'update'} university`);
      }
    } catch (error) {
      console.error(`Error ${isNewMode ? 'creating' : 'updating'} university:`, error);
      toast.error(`Failed to ${isNewMode ? 'create' : 'update'} university`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-ux-cyan" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/universities')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-ux-charcoal">{isNewMode ? 'Add New University' : 'Edit University'}</h1>
              <p className="text-sm text-ux-charcoal/60">{isNewMode ? 'Create a new university entry' : university.name}</p>
            </div>
          </div>
          {canWrite && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isNewMode ? 'Create University' : 'Save Changes'}
            </Button>
          )}
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label>University Name *</Label>
                  <Input
                    value={university.name}
                    onChange={(e) => setUniversity({ ...university, name: e.target.value })}
                    placeholder="e.g., Massachusetts Institute of Technology"
                  />
                </div>
                <div>
                  <Label>Short Name</Label>
                  <Input
                    value={university.short_name}
                    onChange={(e) => setUniversity({ ...university, short_name: e.target.value })}
                    placeholder="e.g., MIT"
                  />
                </div>
                <div>
                  <Label>Country *</Label>
                  <Select
                    value={university.country}
                    onValueChange={(value) => setUniversity({ ...university, country: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={university.city}
                    onChange={(e) => setUniversity({ ...university, city: e.target.value })}
                    placeholder="e.g., Cambridge"
                  />
                </div>
                <div>
                  <Label>State/Province</Label>
                  <Input
                    value={university.state_province}
                    onChange={(e) => setUniversity({ ...university, state_province: e.target.value })}
                    placeholder="e.g., Massachusetts"
                  />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={university.description}
                  onChange={(e) => setUniversity({ ...university, description: e.target.value })}
                  placeholder="Brief description of the university..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Links & Media */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LinkIcon className="w-5 h-5" />
                Links & Media
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Website URL</Label>
                  <Input
                    value={university.website}
                    onChange={(e) => setUniversity({ ...university, website: e.target.value })}
                    placeholder="https://www.university.edu"
                  />
                </div>
                <div>
                  <ImageFieldWithPicker
                    label="University Logo"
                    value={university.logo_url}
                    onChange={(url) => setUniversity({ ...university, logo_url: url })}
                    onClear={() => setUniversity({ ...university, logo_url: '' })}
                    uploadCategory="branding"
                    uploadAssetKeyPrefix="university_logo"
                    placeholder="Select or paste logo URL"
                    previewSize="w-16 h-16"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rankings & Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                Rankings & Statistics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>QS World Ranking</Label>
                  <Input
                    type="number"
                    value={university.qs_rank}
                    onChange={(e) => setUniversity({ ...university, qs_rank: e.target.value })}
                    placeholder="e.g., 1"
                  />
                </div>
                <div>
                  <Label>Acceptance Rate (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={university.acceptance_rate}
                    onChange={(e) => setUniversity({ ...university, acceptance_rate: e.target.value })}
                    placeholder="e.g., 7.3"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Costs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Estimated Costs (USD per year)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tuition Min ($)</Label>
                  <Input
                    type="number"
                    value={university.tuition_min}
                    onChange={(e) => setUniversity({ ...university, tuition_min: e.target.value })}
                    placeholder="e.g., 20000"
                  />
                </div>
                <div>
                  <Label>Tuition Max ($)</Label>
                  <Input
                    type="number"
                    value={university.tuition_max}
                    onChange={(e) => setUniversity({ ...university, tuition_max: e.target.value })}
                    placeholder="e.g., 55000"
                  />
                </div>
                <div>
                  <Label>Living Cost Min ($)</Label>
                  <Input
                    type="number"
                    value={university.living_cost_min}
                    onChange={(e) => setUniversity({ ...university, living_cost_min: e.target.value })}
                    placeholder="e.g., 15000"
                  />
                </div>
                <div>
                  <Label>Living Cost Max ($)</Label>
                  <Input
                    type="number"
                    value={university.living_cost_max}
                    onChange={(e) => setUniversity({ ...university, living_cost_max: e.target.value })}
                    placeholder="e.g., 25000"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status */}
          <Card>
            <CardHeader>
              <CardTitle>Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-8">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={university.is_featured}
                    onCheckedChange={(checked) => setUniversity({ ...university, is_featured: checked })}
                    id="featured"
                  />
                  <Label htmlFor="featured" className="cursor-pointer">
                    Featured University
                  </Label>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={university.is_partner}
                    onCheckedChange={(checked) => setUniversity({ ...university, is_partner: checked })}
                    id="partner"
                  />
                  <Label htmlFor="partner" className="cursor-pointer">
                    Partner University
                  </Label>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UniversityEditPage;
